import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getUser, clearAuth, getAuthToken } from '@/utils/auth';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  Building2, Palette, Key, LogOut, Heart, Home, Trash2, Download, AlertTriangle, Settings,
  ShoppingCart, Tag, Building, FileText, Clock, IndianRupee, MapPin, Camera, Loader2, HelpCircle
} from 'lucide-react';
import HelpReportDialog from '@/components/HelpReportDialog';
import NotificationPreferencesSection from '@/components/NotificationPreferencesSection';
import LoginSessionsSection from '@/components/LoginSessionsSection';
import PreferencesTab from '@/components/PreferencesTab';
import OnboardingTutorial from '@/components/OnboardingTutorial';
import RecommendedProperties from '@/components/RecommendedProperties';
import SellerSubscriptionCards from '@/components/common/SellerSubscriptionCards';

// Settings Tab Component with Delete Account functionality
const SettingsTab = ({ user, navigate, onUserUpdate, onReplayTutorial }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [profileSaving, setProfileSaving] = useState(false);
  // Password change state
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [logoutAllChecked, setLogoutAllChecked] = useState(true);
  const [showPwForm, setShowPwForm] = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  // Import AvatarUpload dynamically
  const AvatarUpload = React.lazy(() => import('@/components/AvatarUpload'));

  const handleAvatarUpdate = (avatarData) => {
    if (onUserUpdate) {
      onUserUpdate({ ...user, avatar: avatarData });
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const res = await apiClient.put('/account/profile', profileForm);
      if (res.data?.user) {
        onUserUpdate?.({ ...user, ...res.data.user });
      }
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 8) return toast.error('New password must be at least 8 characters');
    if (pwForm.new_password !== pwForm.confirm_password) return toast.error('Passwords do not match');
    setPwLoading(true);
    try {
      const res = await apiClient.post('/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
        logout_all: logoutAllChecked,
      });
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
      }
      toast.success(res.data?.message || 'Password changed');
      setShowPwForm(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    setLogoutAllLoading(true);
    try {
      const res = await apiClient.post('/auth/logout-all');
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
      }
      toast.success(res.data?.message || 'Logged out of all devices');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log out');
    } finally {
      setLogoutAllLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await apiClient.get('/account/data-export');
      const data = response.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zenstudios_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Your data has been exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleteLoading(true);
    try {
      await apiClient.delete('/account/delete', {
        data: {
          confirmation: 'DELETE',
          reason: reason || null,
          feedback: feedback || null
        }
      });
      
      toast.success('Your account has been deleted');
      clearAuth();
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <TabsContent value="settings" className="mt-8" data-testid="settings-tab">
      <h2 className="text-2xl mb-6">Account Settings</h2>
      
      <div className="space-y-6">
        {/* Profile Picture */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <Camera className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Profile Picture</h3>
          </div>
          <React.Suspense fallback={<div className="h-24 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}>
            <AvatarUpload user={user} onAvatarUpdate={handleAvatarUpdate} />
          </React.Suspense>
        </div>

        {/* Profile Information */}
        <div className="bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold">Profile Information</h3>
            </div>
            {!editing && (
              <Button variant="outline" size="sm" className="rounded-none text-xs"
                onClick={() => { setEditing(true); setProfileForm({ name: user?.name || '', phone: user?.phone || '' }); }}
                data-testid="edit-profile-btn"
              >Edit</Button>
            )}
          </div>
          {editing ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input id="edit-name" value={profileForm.name}
                  onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                  data-testid="edit-name-input" />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input id="edit-phone" value={profileForm.phone}
                  onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                  data-testid="edit-phone-input" />
              </div>
              <div>
                <Label>Email</Label>
                <p className="text-sm text-gray-500 mt-1">{user?.email} (cannot be changed)</p>
              </div>
              <div className="flex gap-3">
                <Button className="rounded-none" disabled={profileSaving} onClick={handleSaveProfile} data-testid="save-profile-btn">
                  {profileSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" className="rounded-none" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Name</p>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div>
                <p className="text-gray-500">Phone</p>
                <p className="font-medium">{user?.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-gray-500">Account Type</p>
                <p className="font-medium capitalize">{
                  user?.user_type === 'agent' ? 'Agent / Channel Partner' :
                  user?.user_type === 'developer' ? 'Builder / Developer' :
                  user?.user_type === 'employee' ? 'Employee' :
                  user?.user_type === 'admin' ? 'Administrator' :
                  'Individual'
                }</p>
              </div>
            </div>
          )}
        </div>

        {/* Security — Password Change & Session Control */}
        <div className="bg-white p-6" data-testid="security-section">
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Security</h3>
          </div>

          {/* Change Password */}
          {user?.auth_provider !== 'google' && (
            <div className="mb-6">
              {!showPwForm ? (
                <Button variant="outline" className="rounded-none text-sm" onClick={() => setShowPwForm(true)} data-testid="change-password-btn">
                  Change Password
                </Button>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
                  <div>
                    <Label htmlFor="current-pw">Current Password</Label>
                    <Input id="current-pw" type="password" value={pwForm.current_password}
                      onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                      required data-testid="current-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="new-pw">New Password</Label>
                    <Input id="new-pw" type="password" value={pwForm.new_password}
                      onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                      placeholder="At least 8 characters" required minLength={8} data-testid="new-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="confirm-pw">Confirm New Password</Label>
                    <Input id="confirm-pw" type="password" value={pwForm.confirm_password}
                      onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))}
                      required minLength={8} data-testid="confirm-new-password-input" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                    <input type="checkbox" checked={logoutAllChecked} onChange={e => setLogoutAllChecked(e.target.checked)}
                      className="accent-green-700 w-4 h-4" data-testid="logout-all-checkbox" />
                    Log out from all other devices
                  </label>
                  <div className="flex gap-3 pt-1">
                    <Button type="submit" disabled={pwLoading} className="rounded-none bg-green-700 hover:bg-green-800" data-testid="save-password-btn">
                      {pwLoading ? 'Updating...' : 'Update Password'}
                    </Button>
                    <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowPwForm(false); setPwForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                      data-testid="cancel-password-btn">Cancel</Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Logout from all devices */}
          <div className="pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600 mb-3">If you suspect unauthorized access, log out of all devices. You will need to sign in again on every device.</p>
            <Button variant="outline" className="rounded-none text-red-600 border-red-200 hover:bg-red-50"
              disabled={logoutAllLoading} onClick={handleLogoutAll} data-testid="logout-all-devices-btn">
              <LogOut className="w-4 h-4 mr-2" />
              {logoutAllLoading ? 'Logging out...' : 'Log out from all devices'}
            </Button>
          </div>
        </div>

        {/* Data Export */}

        {/* Login Activity */}
        <LoginSessionsSection />

        {/* Notification Preferences */}
        <NotificationPreferencesSection />

        {/* Tutorial */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Tutorial</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Replay the onboarding tutorial to learn about ZenStudios features.
          </p>
          <Button
            onClick={onReplayTutorial}
            variant="outline"
            className="rounded-none border-[#15803D] text-[#15803D] hover:bg-[#15803D]/5"
            data-testid="replay-tutorial-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Replay Tutorial
          </Button>
        </div>

        {/* Data Export */}
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <Download className="w-5 h-5 text-[#15803D]" />
            <h3 className="text-lg font-semibold">Export Your Data</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Download a copy of all your personal data stored with ZenStudios. 
            This includes your profile, listings, quotes, and activity history.
          </p>
          <Button
            onClick={handleExportData}
            disabled={exportLoading}
            variant="outline"
            className="rounded-none border-[#15803D] text-[#15803D] hover:bg-[#15803D]/5"
            data-testid="export-data-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exportLoading ? 'Exporting...' : 'Download My Data'}
          </Button>
        </div>

        {/* Delete Account */}
        <div className="bg-white p-6 border-2 border-red-100">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-red-600">Delete Account</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
            Your listings, quotes, and all personal information will be permanently removed.
          </p>
          
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="destructive"
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]" data-testid="delete-account-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Delete Account Permanently
                </DialogTitle>
                <DialogDescription>
                  This action is irreversible. All your data will be permanently deleted.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded text-sm">
                  <p className="font-semibold text-red-800 mb-2">This will permanently delete:</p>
                  <ul className="list-disc ml-4 text-red-700 space-y-1">
                    <li>Your profile and account information</li>
                    <li>All property listings you have created</li>
                    <li>Your interior design quotes and projects</li>
                    <li>Property management requests</li>
                    <li>Saved searches and wishlist</li>
                  </ul>
                </div>

                <div>
                  <Label htmlFor="reason">Reason for leaving (optional)</Label>
                  <select
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full mt-1 p-2 border rounded text-sm"
                    data-testid="delete-reason-select"
                  >
                    <option value="">Select a reason...</option>
                    <option value="not_using">Not using the service anymore</option>
                    <option value="found_alternative">Found an alternative</option>
                    <option value="privacy_concerns">Privacy concerns</option>
                    <option value="too_many_emails">Too many emails</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="feedback">Additional feedback (optional)</Label>
                  <Textarea
                    id="feedback"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Help us improve..."
                    className="mt-1"
                    data-testid="delete-feedback-input"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmation" className="text-red-600 font-semibold">
                    Type DELETE to confirm
                  </Label>
                  <Input
                    id="confirmation"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder="DELETE"
                    className="mt-1 border-red-300 focus:border-red-500"
                    data-testid="delete-confirmation-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(false)}
                  className="rounded-none"
                  data-testid="delete-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={confirmation !== 'DELETE' || deleteLoading}
                  className="rounded-none bg-red-600 hover:bg-red-700"
                  data-testid="delete-confirm-btn"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Forever'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </TabsContent>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const initialUser = getUser();
  const [user, setUser] = useState(initialUser);
  const [myListings, setMyListings] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [designProjects, setDesignProjects] = useState([]);
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [managedProperties, setManagedProperties] = useState([]);
  const [interiorProject, setInteriorProject] = useState(null);
  const [paymentsOverview, setPaymentsOverview] = useState({ pending: [], completed: [], totals: {} });
  const [customerActions, setCustomerActions] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  // Handle user data updates (e.g., avatar change)
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    // Update local storage
    if (updatedUser) {
      localStorage.setItem('zenstudios_user', JSON.stringify(updatedUser));
    }
  };

  useEffect(() => {
    fetchDashboardData();
    checkTutorial();
  }, []);

  const checkTutorial = async () => {
    try {
      const res = await apiClient.get('/account/preferences');
      if (!res.data.tutorial_completed) setShowTutorial(true);
    } catch (e) { /* ignore */ }
  };

  const handleTutorialComplete = async () => {
    try { await apiClient.post('/account/tutorial-completed'); } catch (e) { /* ignore */ }
    setShowTutorial(false);
  };

  const fetchDashboardData = async () => {
    try {
      const [listings, wishlistData, projects, quotes, managed, interiorProjRes, paymentsRes, actionsRes] = await Promise.all([
        apiClient.get('/properties/my/listings').catch(() => ({ data: [] })),
        apiClient.get('/properties/wishlist/my').catch(() => ({ data: [] })),
        apiClient.get('/interior-design/projects/my').catch(() => ({ data: [] })),
        apiClient.get('/interior-design/quotes/my').catch(() => ({ data: [] })),
        apiClient.get('/property-management/my').catch(() => ({ data: [] })),
        apiClient.get('/interior-projects/my-project').catch(() => ({ data: { projects: [] } })),
        apiClient.get('/customer/payments/overview').catch(() => ({ data: { pending: [], completed: [], totals: {} } })),
        apiClient.get('/customer/actions').catch(() => ({ data: { actions: [] } })),
      ]);

      setMyListings(listings.data);
      setDesignProjects(projects.data);
      setSavedQuotes(quotes.data);
      setManagedProperties(managed.data);
      setPaymentsOverview(paymentsRes.data || { pending: [], completed: [], totals: {} });
      setCustomerActions(actionsRes.data?.actions || []);
      if (interiorProjRes.data?.projects?.length > 0) {
        setInteriorProject(interiorProjRes.data.projects[0]);
      }
      
      // Handle wishlist - check backend first, fallback to local storage
      if (wishlistData.data && wishlistData.data.length > 0) {
        setWishlist(wishlistData.data);
      } else {
        // Fallback to local storage for guests or if backend returns empty
        const localWishlist = localStorage.getItem('zenstudios_wishlist');
        if (localWishlist) {
          const propertyIds = JSON.parse(localWishlist);
          if (propertyIds.length > 0) {
            try {
              // Fetch property details for local wishlist IDs
              const response = await apiClient.post('/properties/by-ids', { property_ids: propertyIds });
              setWishlist(response.data || []);
            } catch (err) {
              console.error('Error fetching wishlist properties:', err);
              setWishlist([]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Still try to load local wishlist on error
      const localWishlist = localStorage.getItem('zenstudios_wishlist');
      if (localWishlist) {
        const propertyIds = JSON.parse(localWishlist);
        if (propertyIds.length > 0) {
          try {
            const response = await apiClient.post('/properties/by-ids', { property_ids: propertyIds });
            setWishlist(response.data || []);
          } catch (err) {
            setWishlist([]);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
      clearAuth();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      clearAuth();
      navigate('/');
    }
  };

  // -------- Razorpay launcher (V4.16 unified payments) --------
  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const launchRazorpay = async ({ order_id, razorpay_order_id, razorpay_key, amount, is_mock, description }) => {
    if (is_mock) {
      // Mock path: just mark paid via verify-service with dummy payload.
      await apiClient.post('/payments/verify-service', {
        order_id,
        razorpay_order_id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_signature: 'mock_sig',
      });
      toast.success('Mock payment completed');
      await fetchDashboardData();
      return;
    }
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error('Could not load Razorpay checkout');
      return;
    }
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: razorpay_key,
        amount: amount * 100,
        currency: 'INR',
        order_id: razorpay_order_id,
        name: 'ZenStudios',
        description: description || 'Payment',
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#15803D' },
        handler: async (response) => {
          try {
            await apiClient.post('/payments/verify-service', {
              order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Payment successful');
            await fetchDashboardData();
            resolve(true);
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Payment verification failed');
            resolve(false);
          }
        },
        modal: {
          ondismiss: () => { toast.info('Payment cancelled'); resolve(false); },
        },
      });
      rzp.open();
    });
  };

  const handlePayItem = async (item) => {
    if (!item?.payable || !item?.razorpay_order) return;
    setPayingId(item.id);
    try {
      await launchRazorpay({ ...item.razorpay_order, description: item.title });
    } finally {
      setPayingId(null);
    }
  };

  const handlePayAll = async () => {
    setPayingId('__bundle__');
    try {
      const { data } = await apiClient.post('/customer/payments/pay-all');
      await launchRazorpay({ ...data, description: `Pay All (${data.bundle_count})` });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not bundle payments');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] py-32" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="text-5xl mb-2" data-testid="dashboard-title">Welcome, {user?.name}</h1>
            <p className="text-gray-600">{user?.email}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="rounded-none"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-7 rounded-none bg-white">
            <TabsTrigger value="overview" className="rounded-none" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="properties" className="rounded-none" data-testid="tab-properties">Real Estate</TabsTrigger>
            <TabsTrigger value="design" className="rounded-none" data-testid="tab-design">Interior Design</TabsTrigger>
            <TabsTrigger value="management" className="rounded-none" data-testid="tab-management">Management</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-none" data-testid="tab-payments">
              Payments{paymentsOverview?.totals?.pending_count ? ` (${paymentsOverview.totals.pending_count})` : ''}
            </TabsTrigger>
            <TabsTrigger value="preferences" className="rounded-none" data-testid="tab-preferences">Preferences</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8" data-testid="overview-tab">
            {customerActions.length > 0 && (
              <div className="bg-white border-l-4 border-[#15803D] p-5 mb-6" data-testid="actions-banner">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-[#15803D]" />
                  <h3 className="text-lg font-semibold">You have {customerActions.length} action{customerActions.length === 1 ? '' : 's'} pending</h3>
                </div>
                <div className="space-y-2">
                  {customerActions.slice(0, 4).map((a, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3" data-testid={`action-${a.type}`}>
                      <div>
                        <p className="text-sm font-medium">{a.title}</p>
                        {a.subtitle && <p className="text-xs text-gray-500">{a.subtitle}</p>}
                      </div>
                      {a.cta && a.link?.includes('tab=payments') && (
                        <Button
                          size="sm"
                          onClick={() => { document.querySelector('[data-testid="tab-payments"]')?.click(); }}
                          className="rounded-none bg-[#15803D] text-white hover:bg-[#14532D]"
                          data-testid={`action-cta-${idx}`}
                        >
                          {a.cta}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-5 gap-6">
              <div className="bg-white p-6">
                <Building2 className="w-8 h-8 text-[#15803D] mb-4" />
                <p className="text-3xl font-bold mb-1">{myListings.length}</p>
                <p className="text-sm text-gray-600">My Listings</p>
              </div>
              <div className="bg-white p-6">
                <Heart className="w-8 h-8 text-[#15803D] mb-4" />
                <p className="text-3xl font-bold mb-1">{wishlist.length}</p>
                <p className="text-sm text-gray-600">Wishlisted</p>
              </div>
              <div className="bg-white p-6">
                <Palette className="w-8 h-8 text-[#15803D] mb-4" />
                <p className="text-3xl font-bold mb-1">{designProjects.length}</p>
                <p className="text-sm text-gray-600">Design Projects</p>
              </div>
              <div className="bg-white p-6">
                <Key className="w-8 h-8 text-[#15803D] mb-4" />
                <p className="text-3xl font-bold mb-1">{managedProperties.length}</p>
                <p className="text-sm text-gray-600">Managed Properties</p>
              </div>
              <div
                className={`bg-white p-6 cursor-pointer hover:shadow-md transition-shadow ${
                  paymentsOverview?.totals?.pending_count ? 'border-l-4 border-[#15803D]' : ''
                }`}
                onClick={() => document.querySelector('[data-testid="tab-payments"]')?.click()}
                data-testid="overview-payments-stat"
              >
                <IndianRupee className="w-8 h-8 text-[#15803D] mb-4" />
                <p className="text-3xl font-bold mb-1">{paymentsOverview?.totals?.pending_count || 0}</p>
                <p className="text-sm text-gray-600">Pending Payments</p>
                {paymentsOverview?.totals?.pending_amount > 0 && (
                  <p className="text-xs text-[#15803D] font-semibold mt-1">
                    ₹{Number(paymentsOverview.totals.pending_amount).toLocaleString('en-IN')} due
                  </p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {/* Seller Subscription Cards */}
              <div className="md:col-span-1">
                <SellerSubscriptionCards token={getAuthToken()} />
              </div>
              <div className="md:col-span-2 grid grid-cols-3 gap-6">
                <Button 
                  onClick={() => navigate('/real-estate')}
                  className="rounded-none bg-primary h-32 text-lg"
                  data-testid="quick-browse-btn"
                >
                  <Home className="w-6 h-6 mr-2" />
                  Browse Properties
                </Button>
                <Button 
                  onClick={() => navigate('/interior-design')}
                  className="rounded-none bg-primary h-32 text-lg"
                  data-testid="quick-quote-btn"
                >
                  <Palette className="w-6 h-6 mr-2" />
                  Get Design Quote
                </Button>
                <Button 
                  onClick={() => navigate('/property-management')}
                  className="rounded-none bg-primary h-32 text-lg"
                  data-testid="quick-manage-btn"
                >
                  <Key className="w-6 h-6 mr-2" />
                  Manage Property
                </Button>
              </div>
            </div>
            
            <RecommendedProperties />
          </TabsContent>

          <TabsContent value="properties" className="mt-8" data-testid="properties-tab">
            {/* Buy Section - Wishlist */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <ShoppingCart className="w-6 h-6 text-[#15803D]" />
                <h2 className="text-2xl">Buy - Wishlist ({wishlist.length})</h2>
              </div>
              {wishlist.length === 0 ? (
                <div className="bg-white p-8 text-center">
                  <p className="text-gray-600 mb-4">No properties saved to wishlist</p>
                  <Button onClick={() => navigate('/buy/properties')} className="rounded-none" data-testid="browse-buy-btn">
                    Browse Properties to Buy
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-6">
                  {wishlist.map((property) => (
                    <div 
                      key={property.property_id} 
                      className="bg-white p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-[#15803D]"
                      onClick={() => navigate(`/property/${property.property_id}`)}
                      data-testid={`wishlist-${property.property_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold">{property.title}</h3>
                        <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                        <MapPin className="w-4 h-4" />
                        {property.city}
                      </div>
                      <p className="font-mono text-xl text-[#15803D]">
                        <IndianRupee className="w-4 h-4 inline" />
                        {property.price >= 10000000 
                          ? `${(property.price / 10000000).toFixed(2)} Cr` 
                          : `${(property.price / 100000).toFixed(2)} L`}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sell Section - My Listings */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Tag className="w-6 h-6 text-[#15803D]" />
                <h2 className="text-2xl">Sell - My Listings ({myListings.filter(p => p.listing_type === 'sell').length})</h2>
              </div>
              {myListings.filter(p => p.listing_type === 'sell').length === 0 ? (
                <div className="bg-white p-8 text-center">
                  <p className="text-gray-600 mb-4">No properties listed for sale</p>
                  <Button onClick={() => navigate('/sell/individual')} className="rounded-none" data-testid="list-sell-btn">
                    List Property for Sale
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-6">
                  {myListings.filter(p => p.listing_type === 'sell').map((property) => (
                    <div key={property.property_id} className="bg-white p-6 border-l-4 border-amber-500" data-testid={`sell-${property.property_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold">{property.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${
                          property.status === 'active' ? 'bg-green-100 text-green-700' :
                          property.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{property.status}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                        <MapPin className="w-4 h-4" />
                        {property.city}
                      </div>
                      <p className="font-mono text-xl text-[#15803D]">
                        <IndianRupee className="w-4 h-4 inline" />
                        {property.price >= 10000000 
                          ? `${(property.price / 10000000).toFixed(2)} Cr` 
                          : `${(property.price / 100000).toFixed(2)} L`}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">Views: {property.views || 0}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rent Section - Rental Listings */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Building className="w-6 h-6 text-[#15803D]" />
                <h2 className="text-2xl">Rent - My Rental Listings ({myListings.filter(p => p.listing_type === 'rent').length})</h2>
              </div>
              {myListings.filter(p => p.listing_type === 'rent').length === 0 ? (
                <div className="bg-white p-8 text-center">
                  <p className="text-gray-600 mb-4">No properties listed for rent</p>
                  <Button onClick={() => navigate('/list-property')} className="rounded-none" data-testid="list-rent-btn">
                    List Property for Rent
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-6">
                  {myListings.filter(p => p.listing_type === 'rent').map((property) => (
                    <div key={property.property_id} className="bg-white p-6 border-l-4 border-blue-500" data-testid={`rent-${property.property_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold">{property.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${
                          property.status === 'active' ? 'bg-green-100 text-green-700' :
                          property.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{property.status}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                        <MapPin className="w-4 h-4" />
                        {property.city}
                      </div>
                      <p className="font-mono text-xl text-[#15803D]">
                        <IndianRupee className="w-4 h-4 inline" />
                        {(property.price || property.rent)?.toLocaleString('en-IN')}/mo
                      </p>
                      <p className="text-xs text-gray-500 mt-2">Views: {property.views || 0}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="design" className="mt-8" data-testid="design-tab">
            {/* Active Interior Project Progress */}
            {interiorProject && (
              <div className="mb-10" data-testid="my-interior-project">
                <div className="flex items-center gap-3 mb-4">
                  <Palette className="w-6 h-6 text-[#15803D]" />
                  <h2 className="text-2xl">My Interior Project</h2>
                </div>
                <div className="bg-white p-6 border-l-4 border-[#15803D]">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{interiorProject.customer_name}</h3>
                      <p className="text-sm text-gray-600">{interiorProject.property_address}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded capitalize">
                      {(interiorProject.project_status || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-4 gap-4 text-sm mb-4">
                    <div><p className="text-gray-500">Start Date</p><p className="font-medium">{interiorProject.start_date || '-'}</p></div>
                    <div><p className="text-gray-500">Est. Completion</p><p className="font-medium">{interiorProject.estimated_completion_date || '-'}</p></div>
                    <div><p className="text-gray-500">Designer</p><p className="font-medium">{interiorProject.assigned_designer || '-'}</p></div>
                    <div><p className="text-gray-500">Site Engineer</p><p className="font-medium">{interiorProject.assigned_site_engineer || '-'}</p></div>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Project Progress</span>
                      <span className="font-bold text-[#15803D]" data-testid="consumer-project-progress">{interiorProject.progress_percent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className="bg-[#15803D] h-2.5 rounded-full transition-all" style={{ width: `${interiorProject.progress_percent}%` }} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(interiorProject.milestones || []).map((ms) => (
                      <div key={ms.milestone_id} className="flex items-start gap-3">
                        <span className="mt-0.5 flex-shrink-0">
                          {ms.milestone_status === 'completed' ? <span className="text-[#15803D]" aria-label="completed">&#10004;</span> :
                           ms.milestone_status === 'in_progress' ? <span className="text-amber-500" aria-label="in progress">&#x1F504;</span> :
                           <span className="text-gray-300" aria-label="pending">&#x2B1C;</span>}
                        </span>
                        <div className="flex-1">
                          <p className={`text-sm ${ms.milestone_status === 'completed' ? 'text-[#15803D] font-medium' : ms.milestone_status === 'in_progress' ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                            {ms.milestone_name}
                          </p>
                          {ms.photos && ms.photos.length > 0 && (
                            <div className="flex gap-2 mt-1 overflow-x-auto">
                              {ms.photos.map((photo, pi) => (
                                <img
                                  key={pi}
                                  src={photo.image_url}
                                  alt={photo.caption || ms.milestone_name}
                                  className="w-14 h-14 rounded object-cover flex-shrink-0 border"
                                  loading="lazy"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Saved Quotes Section */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-[#15803D]" />
                <h2 className="text-2xl">Saved Quotes ({savedQuotes.length})</h2>
              </div>
              {savedQuotes.length === 0 ? (
                <div className="bg-white p-8 text-center">
                  <p className="text-gray-600 mb-4">No saved quotes</p>
                  <Button onClick={() => navigate('/interior-design')} className="rounded-none" data-testid="get-quote-btn">
                    Get Design Quote
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {savedQuotes.map((quote) => (
                    <div key={quote.quote_id} className="bg-white p-6 border-l-4 border-[#15803D]" data-testid={`quote-${quote.quote_id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-lg font-semibold capitalize">{quote.variant} Style</h3>
                          <p className="text-xs text-gray-500">Quote ID: {quote.quote_id}</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                          {quote.status || 'saved'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                        <Clock className="w-4 h-4" />
                        {new Date(quote.saved_at || quote.created_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', month: 'short', year: 'numeric' 
                        })}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <span className="text-sm text-gray-600">{quote.room_breakdowns?.length || 0} rooms</span>
                        <p className="font-mono text-xl text-[#15803D]">
                          <IndianRupee className="w-4 h-4 inline" />
                          {(quote.total_bill || quote.total_amount)?.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Projects Section */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Palette className="w-6 h-6 text-[#15803D]" />
                <h2 className="text-2xl">Active Projects ({designProjects.length})</h2>
              </div>
              {designProjects.length === 0 ? (
                <div className="bg-white p-8 text-center">
                  <p className="text-gray-600 mb-4">No active design projects</p>
                  <p className="text-sm text-gray-500">Save a quote and book a consultation to start a project</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {designProjects.map((project) => (
                    <div key={project.project_id} className="bg-white p-8" data-testid={`project-${project.project_id}`}>
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h3 className="text-2xl mb-2">{project.title}</h3>
                          <p className="text-sm text-gray-600">Status: {project.status}</p>
                        </div>
                        <span className="px-4 py-2 bg-[#F5F5F0] text-sm">{project.current_phase}</span>
                      </div>
                      <div className="grid grid-cols-6 gap-2">
                        {project.phases.map((phase, idx) => (
                          <div key={idx} className="text-center">
                            <div className={`h-2 mb-2 ${
                              phase.status === 'in_progress' ? 'bg-[#4ADE80]' :
                              phase.status === 'completed' ? 'bg-[#15803D]' :
                              'bg-gray-200'
                            }`} />
                            <p className="text-xs text-gray-600">{phase.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="management" className="mt-8" data-testid="management-tab">
            <h2 className="text-2xl mb-4">Managed Properties ({managedProperties.length})</h2>
            {managedProperties.length === 0 ? (
              <div className="bg-white p-12 text-center">
                <p className="text-gray-600 mb-4">No properties under management</p>
                <Button onClick={() => navigate('/property-management')} className="rounded-none" data-testid="request-mgmt-btn">
                  Request Management Service
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {managedProperties.map((property) => (
                  <div key={property.management_id} className="bg-white p-8" data-testid={`managed-${property.management_id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl mb-2">{property.property_address}</h3>
                        <p className="text-sm text-gray-600 mb-2">{property.service_type.replace('_', ' ').toUpperCase()}</p>
                        <p className="text-xs text-gray-500">Status: {property.status}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-2xl text-[#15803D]">₹{property.monthly_fee.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-gray-500">per month</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="payments" className="mt-8" data-testid="payments-tab">
            <PaymentsTabContent
              overview={paymentsOverview}
              payingId={payingId}
              onPay={handlePayItem}
              onPayAll={handlePayAll}
              onRefresh={fetchDashboardData}
            />
          </TabsContent>

          <PreferencesTab />
          <SettingsTab user={user} navigate={navigate} onUserUpdate={handleUserUpdate} onReplayTutorial={() => setShowTutorial(true)} />
        </Tabs>
      </div>

      {/* Help FAB */}
      <button onClick={() => setHelpOpen(true)} className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110" data-testid="help-fab-btn" title="Help & Support">
        <HelpCircle className="w-5 h-5" />
      </button>
      <HelpReportDialog open={helpOpen} onOpenChange={setHelpOpen} portal="consumer" user={user} apiClient={apiClient} apiPrefix="" />
      <OnboardingTutorial portalType="consumer" open={showTutorial} onClose={() => setShowTutorial(false)} onComplete={handleTutorialComplete} />
    </div>
  );
};

export default Dashboard;

// ============================================================
// Payments Tab (V4.16 — Unified customer payments)
// ============================================================
const formatINRDisplay = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const SOURCE_LABEL = {
  interior: 'Interior Project',
  consultation: 'Consultation',
  service: 'Service',
  subscription: 'Subscription',
  bundle: 'Bundle',
};

const PaymentsTabContent = ({ overview, payingId, onPay, onPayAll, onRefresh }) => {
  const { pending = [], completed = [], totals = {} } = overview || {};
  const hasPending = pending.length > 0;

  return (
    <div className="space-y-8" data-testid="payments-tab-content">
      {/* Summary row */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white p-5 border-l-4 border-[#15803D]" data-testid="payments-kpi-pending">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Pending</p>
          <p className="text-2xl font-bold">{formatINRDisplay(totals.pending_amount)}</p>
          <p className="text-xs text-gray-500 mt-1">{totals.pending_count || 0} item{totals.pending_count === 1 ? '' : 's'} to pay</p>
        </div>
        <div className="bg-white p-5" data-testid="payments-kpi-completed">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Paid (lifetime)</p>
          <p className="text-2xl font-bold">{formatINRDisplay(totals.completed_amount)}</p>
          <p className="text-xs text-gray-500 mt-1">{totals.completed_count || 0} transaction{totals.completed_count === 1 ? '' : 's'}</p>
        </div>
        <div className="bg-white p-5 flex items-center justify-between gap-3">
          {hasPending ? (
            <>
              <div>
                <p className="text-sm font-semibold">Pay everything at once</p>
                <p className="text-xs text-gray-500">Bundle {pending.length} items · {formatINRDisplay(totals.pending_amount)}</p>
              </div>
              <Button
                onClick={onPayAll}
                disabled={payingId === '__bundle__'}
                className="rounded-none bg-[#15803D] text-white hover:bg-[#14532D]"
                data-testid="pay-all-btn"
              >
                {payingId === '__bundle__' ? 'Opening…' : 'Pay All'}
              </Button>
            </>
          ) : (
            <div className="w-full text-center text-sm text-gray-500">All caught up. No pending payments.</div>
          )}
        </div>
      </div>

      {/* Pending list */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#15803D]" />
            <h2 className="text-2xl">Pending Payments</h2>
          </div>
          <button onClick={onRefresh} className="text-xs text-gray-500 hover:text-[#15803D] underline">
            Refresh
          </button>
        </div>
        {!hasPending ? (
          <div className="bg-white p-8 text-center text-sm text-gray-500">
            No pending payments right now.
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((item) => (
              <div
                key={`${item.source}-${item.id}`}
                className="bg-white p-5 flex flex-wrap items-center justify-between gap-3 border-l-4 border-[#15803D]"
                data-testid={`payment-pending-${item.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                    <Tag className="w-3 h-3" />
                    <span>{SOURCE_LABEL[item.source] || item.source}</span>
                    {item.status && item.status !== 'pending' && (
                      <span className="text-amber-600">· {item.status}</span>
                    )}
                  </div>
                  <p className="text-lg font-semibold truncate">{item.title}</p>
                  {item.subtitle && <p className="text-xs text-gray-500 truncate max-w-[500px]">{item.subtitle}</p>}
                  {item.due_date && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Due: {String(item.due_date).slice(0, 10)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-xl font-mono text-[#15803D]">
                    <IndianRupee className="w-4 h-4 inline" />
                    {Number(item.amount || 0).toLocaleString('en-IN')}
                  </p>
                  <Button
                    onClick={() => onPay(item)}
                    disabled={!item.payable || payingId === item.id}
                    className="rounded-none bg-[#15803D] text-white hover:bg-[#14532D] disabled:opacity-50"
                    data-testid={`pay-btn-${item.id}`}
                  >
                    {payingId === item.id ? 'Opening…' : (item.razorpay_order?.is_mock ? 'Pay (Test)' : 'Pay Now')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Completed list */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#15803D]" />
          <h2 className="text-2xl">Payment History</h2>
        </div>
        {completed.length === 0 ? (
          <div className="bg-white p-8 text-center text-sm text-gray-500">
            No past payments yet.
          </div>
        ) : (
          <div className="bg-white divide-y">
            {completed.map((c) => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`payment-completed-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.title}</p>
                  <p className="text-[11px] text-gray-500">
                    {SOURCE_LABEL[c.source] || c.source} · {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : '—'}
                    {c.razorpay_payment_id && <span className="font-mono ml-2">{c.razorpay_payment_id}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-[#15803D]">{formatINRDisplay(c.amount)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">Paid</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};